import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export type RunStatus = 'running' | 'completed' | 'failed';
export type OrgRunState = 'pending' | 'completed' | 'skipped' | 'failed';

export interface OrgRunStatus {
  orgId: string;
  orgName: string;
  state: OrgRunState;
  error?: string;
}

export interface MigrationRun {
  runId: string;
  status: RunStatus;
  startedAt: string;
  completedAt?: string;
  orgStatuses: OrgRunStatus[];
  pollError?: boolean;
}

interface MigrationRunPanelProps {
  run: MigrationRun | null;
}

const RUN_STATUS_BADGE: Record<RunStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  running:   { label: 'Running',   variant: 'default' },
  completed: { label: 'Completed', variant: 'secondary' },
  failed:    { label: 'Failed',    variant: 'destructive' },
};

function fmt(iso: string) {
  return new Date(iso).toLocaleString();
}

export function MigrationRunPanel({ run }: MigrationRunPanelProps) {
  if (!run) {
    return (
      <Card>
        <CardHeader><CardTitle>Run Panel</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            No active run. Select organisations and click "Start migration".
          </p>
        </CardContent>
      </Card>
    );
  }

  const badge = RUN_STATUS_BADGE[run.status];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Migration Run</CardTitle>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {run.pollError && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-3 py-2 rounded-md">
            Lost connection to run — check status manually.
          </div>
        )}

        <div className="space-y-1">
          {run.orgStatuses.length === 0 ? (
            <p className="text-sm text-gray-400">No organisations in this run.</p>
          ) : (
            run.orgStatuses.map(org => {
              const isOrgDone = org.state === 'completed' || org.state === 'skipped' || org.state === 'failed';
              return (
                <p key={org.orgId} className="text-sm text-gray-700">
                  Organisation {org.orgName} status: {org.state}, started at: {fmt(run.startedAt)}
                  {isOrgDone && run.completedAt ? `, completed at: ${fmt(run.completedAt)}` : ''}
                </p>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
